import React, { useCallback, useEffect, useMemo, useState } from "react";
import { ReactSVG } from "react-svg";
import { endOfDay, startOfDay } from "date-fns";
import { useLazyQuery, useQuery } from "@apollo/client";
import { useToast } from "@npm-telluria-tecnologia/telluria-ui";
import { useLocation } from "react-router-dom";

// Hooks
import { useSelectClient } from "@atoms/SelectClient";
import { useAuth } from "@hooks/useAuth";

import type { IConcretingControllerDate } from "@molecules/index";
// molecules
import {
  BaseCard,
  BaseCardAction,
  BaseCardHeader,
  BaseCardItem,
  ConcretingButtons,
  PageHeader,
  TravelsResumeDialog
} from "@molecules/index";

// store
import Store from "@store/jotai";
import { ModulesID } from "@store/constants";

import type { IPendency } from "@atoms/index";
// atoms
import { Loading } from "@atoms/index";
import ShowMoreButton from "src/atoms/ShowMoreButton";
import AlertContentByClient from "@atoms/AlertContentByClient";

import { MapIcon } from "src/assets/icons";

// graphql
import {
  ConcretingQueriesGetAllWithSortedPaginationArgs,
  ConcretingType,
  ECommandResultStatus,
  ESort,
  ETravelStatus,
  EWhereClausesOperators,
  PermissionUserQueriesGetAllWithoutPaginationArgs,
  TravelType
} from "@store/graphql/helpers/types.graphql";
import {
  permissionUserAllWithoutPagination,
  TPermissionAllWithoutPaginationQueries
} from "src/store/graphql/groups/permissionUser/query.graphql";

// Libraries
import { Grid } from "@libraries/mui";
import utils from "@helpers/utils";

// Languages
import { AlertMessages, CustomerMessages } from "@languages/interfaces";
import useTranslation from "@languages/useTranslation";

import {
  concretingAllWithSortedPagination,
  TConcretingAllWithSortedPaginationQueries
} from "@graphql/groups/concreting/query.graphql";
import * as Styled from "./styles";
import Office from "../Office";

interface LocationState {
  travel?: TravelType;
}
const Concreting: React.FC = () => {
  const { t } = useTranslation();
  const location = useLocation();

  const LIMIT_PER_PAGE = 12;
  const LOADING_PAST = "past";
  const LOADING_TODAY = "today";
  const LOADING_FUTURE = "future";

  const [selectedConcretingId, setSelectedConcretingId] = useState(
    (location.state as LocationState)?.travel?.concreting?.id
  );
  const [selectedTravelId, setSelectedTravelId] = useState(
    (location.state as LocationState)?.travel?.id
  );
  const [selectedConcreting, setSelectedConcreting] = useState<ConcretingType | undefined>();
  const [concretingControllerDate, setConcretingControllerDate] = useState<IConcretingControllerDate>(LOADING_TODAY);
  const [whereConcretingIds, setWhereConcretingIds] = useState<string>("");
  const [isTravelsResumeDialogOpen, setIsTravelsResumeDialogOpen] = useState(false);
  const [nextPage, setNextPage] = useState<number>(1);
  const [totalPages, setTotalPages] = useState<number>(0);

  const {
    value: concretings,
    setValue: setConcretings,
    filteredConcretings,
    setFilteredConcretings
  } = Store.useConcretings();
  const { value: selectedClient } = useSelectClient();

  const { addToast } = useToast();
  const { getDecodedToken } = useAuth();
  const token = getDecodedToken();

  const filters = useMemo(() => {
    const defaultFilter = [
      {
        property: "id",
        operator: EWhereClausesOperators.IN,
        value: whereConcretingIds
      }
    ];

    const today = new Date();
    const startDay = startOfDay(today).toISOString();
    const endDay = endOfDay(today).toISOString();
    let dateInUsing = startDay;
    const operatorController = concretingControllerDate === LOADING_TODAY
      ? EWhereClausesOperators.EQUALS
      : concretingControllerDate === LOADING_PAST
        ? EWhereClausesOperators.LESS_THAN
        : EWhereClausesOperators.GREATER_THAN;

    if (concretingControllerDate === LOADING_FUTURE) dateInUsing = endDay;

    if (concretingControllerDate === LOADING_TODAY) {
      return [
        ...defaultFilter,
        {
          property: "scheduledDate",
          operator: EWhereClausesOperators.GREATER_THAN_OR_EQUAL,
          value: startDay
        },
        {
          property: "scheduledDate",
          operator: EWhereClausesOperators.LESS_THAN_OR_EQUAL,
          value: endDay
        }
      ];
    }

    // If changing the filter and the search is for "LOADING_PAST", then resets the page and total pages;
    if (concretingControllerDate === LOADING_PAST) {
      setNextPage(1);
      setTotalPages(0);
    }

    return [
      ...defaultFilter,
      {
        property: "scheduledDate",
        operator: operatorController,
        value: dateInUsing
      }
    ];
  }, [concretingControllerDate, whereConcretingIds]);

  const {
    loading: loadingPermissions,
    error: errorPermissions,
    data: dataPermissions
  } = useQuery<
  TPermissionAllWithoutPaginationQueries,
  PermissionUserQueriesGetAllWithoutPaginationArgs
  >(permissionUserAllWithoutPagination(["contract.concretings.id"]), {
    fetchPolicy: "network-only",
    pollInterval: 60000,
    variables: {
      where: [
        {
          property: "user.id",
          operator: EWhereClausesOperators.EQUALS,
          value: token.primarysid
        },
        {
          property: "construction.client.id",
          operator: EWhereClausesOperators.EQUALS,
          value: selectedClient.id
        },
        {
          property: "module.id",
          operator: EWhereClausesOperators.EQUALS,
          value: ModulesID.CONCRETING
        }
      ]
    }
  });

  const [
    getConcretings,
    {
      loading: loadingConcretings,
      error: errorConcretings,
      data: dataConcretings
    }
  ] = useLazyQuery<
    TConcretingAllWithSortedPaginationQueries,
    ConcretingQueriesGetAllWithSortedPaginationArgs
  >(
    concretingAllWithSortedPagination([
      "id",
      "status",
      "scheduledDate",
      "programmedVolume",
      "deliveredVolume",
      "pendency",
      "contractConcreteRecipe.concreteRecipe.description",
      "contract.construction.name",
      "contract.construction.client.pendency",
      "contract.pendency",
      "travels.id",
      "travels.fleetExternalId",
      "travels.status",
      "travels.loadLabel",
      "travels.documentNumber",
      "travels.volume",
      "travels.scheduledTime",
      "travels.invoiceIssueTime",
      "travels.plantDepartureTime",
      "travels.dischargeStartTime",
      "travels.dischargeEndTime",
      "travels.finishTime",
      "travels.constructionStartTime",
      "travels.constructionEndTime",
      "travels.concreteMixer.description",
      "travels.concreteMixer.code",
      "travels.concreteMixer.licensePlate",
      "travels.driver.name"
    ]),
    {
      fetchPolicy: "network-only",
      pollInterval: 60000
    }
  );

  const loadConcretings = useCallback(async () => {
    const response = dataConcretings?.concreting.getAllWithSortedPagination;

    if (response?.status === ECommandResultStatus.SUCCESS) {
      setTotalPages(response.pageCount);
      const concretingsSorted = response.result;

      if (concretingControllerDate === LOADING_PAST && nextPage > 1) {
        setConcretings(concretings.concat(concretingsSorted as ConcretingType[]));
      } else {
        setConcretings(concretingsSorted as ConcretingType[]);
      }
    }
  }, [dataConcretings, setConcretings]);

  const makeWhereConcretingIds = useCallback(() => {
    const response = dataPermissions?.permissionUser.getAllWithoutPagination;

    if (response?.status === ECommandResultStatus.SUCCESS) {
      const concretingIds: string[] = [];

      response.result?.forEach((permission) => {
        permission?.contract?.concretings?.forEach((concreting) => {
          concretingIds.push(concreting?.id || "");
        });
      });

      const concretingIdsStr = concretingIds
        .filter((id) => id && id !== "")
        .join("|");

      if (!concretingIdsStr || concretingIdsStr === "") {
        setWhereConcretingIds("");
        setFilteredConcretings([]);

        return;
      }

      setWhereConcretingIds(concretingIdsStr);
    }
  }, [dataPermissions, setFilteredConcretings]);

  useEffect(() => {
    if (whereConcretingIds !== "") {
      const variables: ConcretingQueriesGetAllWithSortedPaginationArgs = {
        where: filters,
        sort: [{
          field: "scheduledDate",
          sortDirection: ESort.DESC
        }]
      };

      if (concretingControllerDate === LOADING_PAST) {
        variables.page = nextPage;
        variables.perPage = LIMIT_PER_PAGE;
      }

      getConcretings({ variables });
    }
  }, [getConcretings, whereConcretingIds, filters, nextPage]);

  useEffect(() => {
    if (dataPermissions) makeWhereConcretingIds();
    if (errorPermissions) {
      addToast({
        type: "error",
        title: "Internal client error",
        description: errorPermissions.message
      });
    }
  }, [dataPermissions, errorPermissions, addToast, makeWhereConcretingIds]);

  useEffect(() => {
    if (dataConcretings) loadConcretings();
    if (errorConcretings) {
      addToast({
        type: "error",
        title: "Internal client error",
        description: errorConcretings.message
      });
    }
  }, [addToast, dataConcretings, errorConcretings, loadConcretings]);

  useEffect(() => {
    setConcretings([]);
  }, [selectedClient, filters, setConcretings]);

  useEffect(() => {
    setFilteredConcretings(concretings);
  }, [concretings, setFilteredConcretings]);

  useEffect(() => () => {
    setConcretings([]);
    setFilteredConcretings([]);
  }, [setConcretings, setFilteredConcretings]);

  const detailsConcreting = useCallback((concreting: ConcretingType) => {
    if (!concreting.travels) return undefined;
    const { travels } = concreting;

    const allTravels = utils.groupAndSortTravels(travels);

    return (
      <div>
        {allTravels.map((travel) => {
          if (!travel || travel.status === ETravelStatus.PROGRAMMED) return undefined;

          return <BaseCardItem key={travel.id} type="travel" travel={travel} concretingId={concreting.id} />;
        })}
      </div>
    );
  }, []);

  const actionsConcreting = useCallback(
    (concreting: ConcretingType) => {
      const progressValue = Math.floor(
        (concreting.deliveredVolume * 100) / concreting.programmedVolume
      );
      const hasPendency = !!concreting.pendency
        || !!concreting?.contract?.pendency
        || !!concreting.contract?.construction?.client?.pendency;
      const colorIconAlert = hasPendency ? "#FF0000" : "#BEBEBE";

      const pendencies: IPendency = {};

      if (concreting.pendency) pendencies.concreting = concreting.pendency;
      if (concreting.contract?.pendency) pendencies.contract = concreting.contract.pendency;
      if (concreting.contract?.construction?.client?.pendency) pendencies.client = concreting.contract.construction.client.pendency;

      return (
        <>
          {concretingControllerDate === LOADING_TODAY && (
            <BaseCardAction type="progress" progressValue={progressValue} />
          )}

          <BaseCardAction
            type="pendency"
            color={colorIconAlert}
            hasPendency={hasPendency}
            pendencies={pendencies}
          />

          {concretingControllerDate === LOADING_TODAY && (
            <ReactSVG
              src={MapIcon}
              onClick={() => {
                setIsTravelsResumeDialogOpen(true);
                setSelectedConcreting(concreting);
              }}
            />
          )}

        </>
      );
    },
    [concretingControllerDate]
  );

  const concretingBaseCards = useMemo(() => {
    if (!filteredConcretings) return [];

    return filteredConcretings.map((concreting) => {
      const { travels } = concreting;

      return (
        <Grid key={`grid-${concreting.id}`} item xs={12} sm={6} md={4}>
          <BaseCard
            key={concreting.id}
            id={concreting.id}
            header={<BaseCardHeader type="concreting" data={{ concreting, travels }} />}
            details={detailsConcreting(concreting)}
            actions={actionsConcreting(concreting)}
            startExpanded={selectedConcretingId === concreting.id}
          />
        </Grid>
      );
    });
  }, [
    actionsConcreting,
    detailsConcreting,
    filteredConcretings,
    selectedConcretingId,
    concretings
  ]);

  const travelResumeDialog = useMemo(() => {
    if (selectedConcreting) {
      return (
        <TravelsResumeDialog
          isOpen={isTravelsResumeDialogOpen}
          onClose={() => setIsTravelsResumeDialogOpen(false)}
          concreting={selectedConcreting}
        />
      );
    }

    return null;

  }, [selectedConcreting, isTravelsResumeDialogOpen]);

  // Scroll to card based on id
  useEffect(() => {
    if (concretingBaseCards?.length > 0 && selectedTravelId) {
      setTimeout(() => {
        const travelElement = document.getElementById(selectedTravelId);

        if (travelElement) {
          travelElement.scrollIntoView({ behavior: "smooth" });

          const travelStatus = (location?.state as LocationState)?.travel
            ?.status;

          if (
            travelStatus === ETravelStatus.IN_TRANSIT
            || travelStatus === ETravelStatus.IN_CONSTRUCTION
            || travelStatus === ETravelStatus.DISCHARGING
          ) {
            const travelElementAction = document.getElementById(
              `${selectedTravelId}_action`
            );

            if (travelElementAction) travelElementAction.click();
          }
        }
        setSelectedTravelId(undefined);
        setSelectedConcretingId(undefined);
      }, 500);
    }
  }, [location, selectedTravelId, concretingBaseCards]);

  const handleNextPage = () => setNextPage(nextPage + 1);

  return (
    <Office>
      <PageHeader title={t(CustomerMessages.applicationConcreting)} url="/" />

      <ConcretingButtons
        concretingControllerDate={concretingControllerDate}
        setConcretingControllerDate={setConcretingControllerDate}
      />

      <Styled.Container>
        <Grid container spacing={2}>
          {(loadingConcretings || loadingPermissions) && (
            <Grid item xs={12}>
              <Loading loading />
            </Grid>
          )}
          {concretingBaseCards?.length === 0 ? (
            <Grid item xs={12}>
              <AlertContentByClient alertMessage={t(AlertMessages.noConcreting)} iconColor="yellow" />
            </Grid>
          ) : (
            concretingBaseCards
          )}
        </Grid>
        {
          concretingControllerDate === LOADING_PAST
          && filteredConcretings
          && nextPage < totalPages
          && (
            <Styled.ContainerShowMoreButton>
              <ShowMoreButton onClick={handleNextPage} />
            </Styled.ContainerShowMoreButton>
          )
        }
      </Styled.Container>

      {travelResumeDialog}

    </Office>
  );
};

export default Concreting;
